import { useEffect } from "react";
import { useFoundation } from "./FoundationProvider";
import { SYNC_OPERATION_STATUSES } from "../../domain/valueObjects/syncOperationStatuses";

const AUTOSYNC_POLL_INTERVAL_MS = 30_000;
const AUTOSYNC_IDLE_DELAY_MS = 20_000;
const AUTOSYNC_MIN_INTERVAL_MS = 180_000;

export const useFoundationBootstrap = () => {
  const { localDataAccess, syncEngine } = useFoundation();

  useEffect(() => {
    let disposed = false;
    let pendingTimeoutId = null;
    let isSyncRunning = false;
    let lastSyncStartedAt = 0;

    const clearPendingTimeout = () => {
      if (pendingTimeoutId) {
        window.clearTimeout(pendingTimeoutId);
        pendingTimeoutId = null;
      }
    };

    const listSyncCandidates = async () => {
      const [pending, failed] = await Promise.all([
        localDataAccess.repositories.syncOperations.listByStatus(
          SYNC_OPERATION_STATUSES.PENDING
        ),
        localDataAccess.repositories.syncOperations.listByStatus(
          SYNC_OPERATION_STATUSES.FAILED
        ),
      ]);

      return [...pending, ...failed];
    };

    const runAutosync = async (reason) => {
      if (disposed || isSyncRunning || !window.navigator.onLine) {
        return false;
      }

      const operations = await listSyncCandidates();
      if (operations.length === 0) {
        clearPendingTimeout();
        return false;
      }

      const now = Date.now();
      if (
        reason !== "online" &&
        lastSyncStartedAt > 0 &&
        now - lastSyncStartedAt < AUTOSYNC_MIN_INTERVAL_MS
      ) {
        return false;
      }

      isSyncRunning = true;
      lastSyncStartedAt = now;
      clearPendingTimeout();

      try {
        const results = await syncEngine.runPending();
        const failedCount = results.filter((result) => !result.success).length;

        if (results.length > 0) {
          console.info("[OceanLogix v2 autosync] Processed queued operations", {
            reason,
            count: results.length,
            failedCount,
          });
        }
      } catch (error) {
        // Keep autosync non-fatal while the app is still on the v1 UI surface.
        console.error("OceanLogix v2 foundation autosync failed", error);
      } finally {
        isSyncRunning = false;
      }

      return true;
    };

    const scheduleAutosync = async () => {
      if (disposed || isSyncRunning || pendingTimeoutId || !window.navigator.onLine) {
        return;
      }

      const operations = await listSyncCandidates();
      if (operations.length === 0) {
        clearPendingTimeout();
        return;
      }

      const timeSinceLastSync = Date.now() - lastSyncStartedAt;
      const cooldownRemaining =
        lastSyncStartedAt > 0
          ? Math.max(0, AUTOSYNC_MIN_INTERVAL_MS - timeSinceLastSync)
          : 0;
      const delay = Math.max(AUTOSYNC_IDLE_DELAY_MS, cooldownRemaining);

      pendingTimeoutId = window.setTimeout(() => {
        pendingTimeoutId = null;
        runAutosync("idle").catch((error) => {
          console.error("OceanLogix v2 foundation autosync scheduling failed", error);
        });
      }, delay);
    };

    const handleOnline = () => {
      runAutosync("online").catch((error) => {
        console.error("OceanLogix v2 foundation online autosync failed", error);
      });
    };

    runAutosync("bootstrap").catch((error) => {
      console.error("OceanLogix v2 foundation bootstrap sync failed", error);
    });

    const pollIntervalId = window.setInterval(() => {
      scheduleAutosync().catch((error) => {
        console.error("OceanLogix v2 foundation autosync poll failed", error);
      });
    }, AUTOSYNC_POLL_INTERVAL_MS);

    window.addEventListener("online", handleOnline);

    return () => {
      disposed = true;
      clearPendingTimeout();
      window.clearInterval(pollIntervalId);
      window.removeEventListener("online", handleOnline);
    };
  }, [localDataAccess.repositories.syncOperations, syncEngine]);
};
